import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { typedSupabaseRpc } from '@/shared/lib/typed-rpc'
import type {
  DocumentFolder,
  DocumentFolderWithRelations,
  DocumentFolderCreateInput,
  DocumentFolderUpdateInput,
  FolderTreeNode,
} from '../types'

const FOLDERS_QUERY_KEY = ['document-folders']

/**
 * Générer un slug à partir d'un nom
 */
function generateSlug(name: string): string {
  return name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Remove accents
    .replace(/[^a-z0-9]+/g, '-') // Replace non-alphanumeric with dash
    .replace(/^-+|-+$/g, '') // Remove leading/trailing dashes
}

/**
 * Récupérer tous les dossiers d'une résidence
 */
export function useFolders(residenceId?: string) {
  return useQuery({
    queryKey: [...FOLDERS_QUERY_KEY, residenceId],
    queryFn: async () => {
      if (!residenceId) return []

      const { data, error } = await supabase
        .from('document_folders')
        .select('*')
        .eq('residence_id', residenceId)
        .is('deleted_at', null)
        .order('sort_order', { ascending: true })
        .order('name', { ascending: true })

      if (error) throw error
      return data as DocumentFolder[]
    },
    enabled: !!residenceId,
  })
}

/**
 * Récupérer un dossier par son ID
 */
export function useFolder(folderId?: string) {
  return useQuery({
    queryKey: [...FOLDERS_QUERY_KEY, 'detail', folderId],
    queryFn: async () => {
      if (!folderId) return null

      const { data, error } = await supabase
        .from('document_folders')
        .select('*')
        .eq('id', folderId)
        .is('deleted_at', null)
        .single()

      if (error) throw error
      return data as DocumentFolder
    },
    enabled: !!folderId,
  })
}

/**
 * Construire une arborescence de dossiers
 */
export function useFolderTree(residenceId?: string) {
  const { data: folders = [], ...query } = useFolders(residenceId)

  const tree: FolderTreeNode[] = React.useMemo(() => {
    // Créer une map des dossiers par ID
    const folderMap = new Map<string, DocumentFolderWithRelations>()
    folders.forEach((folder) => {
      folderMap.set(folder.id, { ...folder, children: [] })
    })

    // Construire l'arborescence
    const rootNodes: FolderTreeNode[] = []

    folders.forEach((folder) => {
      const node = folderMap.get(folder.id)!

      if (!folder.parent_folder_id) {
        // Dossier racine
        rootNodes.push({
          folder: node,
          children: [],
          level: 0,
        })
      } else {
        // Sous-dossier
        const parent = folderMap.get(folder.parent_folder_id)
        if (parent) {
          parent.children = parent.children || []
          parent.children.push(node)
        }
      }
    })

    // Fonction récursive pour construire les nœuds de l'arbre
    function buildTreeNodes(
      folderWithChildren: DocumentFolderWithRelations,
      level: number
    ): FolderTreeNode {
      const children = (folderWithChildren.children || []).map((child) =>
        buildTreeNodes(child, level + 1)
      )

      return {
        folder: folderWithChildren,
        children,
        level,
      }
    }

    return rootNodes.map((root) => buildTreeNodes(root.folder, 0))
  }, [folders])

  return {
    ...query,
    data: tree,
    folders,
  }
}

/**
 * Créer un nouveau dossier
 */
export function useCreateFolder() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: DocumentFolderCreateInput) => {
      // Récupérer l'agency_id de la résidence
      const { data: residence, error: residenceError } = await supabase
        .from('residences')
        .select('agency_id')
        .eq('id', input.residence_id)
        .single()

      if (residenceError || !residence) {
        throw new Error('Residence not found')
      }

      // Générer le slug si non fourni
      const slug = input.slug || generateSlug(input.name)

      // Créer le dossier
      const { data, error } = await supabase
        .from('document_folders')
        .insert({
          name: input.name,
          slug,
          description: input.description || null,
          color: input.color || null,
          icon: input.icon || null,
          parent_folder_id: input.parent_folder_id || null,
          residence_id: input.residence_id,
          agency_id: residence.agency_id,
          sort_order: input.sort_order || 0,
          is_system_folder: false,
        })
        .select()
        .single()

      if (error) throw error
      return data as DocumentFolder
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [...FOLDERS_QUERY_KEY, data.residence_id] })
    },
  })
}

/**
 * Mettre à jour un dossier
 */
export function useUpdateFolder() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: DocumentFolderUpdateInput }) => {
      const updateData: DocumentFolderUpdateInput = { ...updates }

      // Générer le slug si le nom change
      if (updates.name && !updates.slug) {
        updateData.slug = generateSlug(updates.name)
      }

      const { data, error } = await supabase
        .from('document_folders')
        .update(updateData)
        .eq('id', id)
        .select()
        .single()

      if (error) throw error
      return data as DocumentFolder
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [...FOLDERS_QUERY_KEY, data.residence_id] })
      queryClient.invalidateQueries({ queryKey: [...FOLDERS_QUERY_KEY, 'detail', data.id] })
    },
  })
}

/**
 * Supprimer un dossier (physique si vide, soft delete sinon)
 */
export function useDeleteFolder() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (folderId: string) => {
      // Récupérer le dossier pour avoir residence_id
      const { data: folder, error: fetchError } = await supabase
        .from('document_folders')
        .select('residence_id')
        .eq('id', folderId)
        .single()

      if (fetchError) throw fetchError

      // Utiliser la fonction RPC smart delete (type-safe wrapper)
      const result = await typedSupabaseRpc.documents.deleteFolderSmart(folderId)

      if (!result.success) {
        throw new Error(result.message || 'Erreur lors de la suppression du dossier')
      }

      return {
        folderId,
        residenceId: folder?.residence_id ?? '',
        deletionType: result.deletion_type ?? 'unknown',
        message: result.message ?? '',
      }
    },
    onSuccess: ({ residenceId }) => {
      queryClient.invalidateQueries({ queryKey: [...FOLDERS_QUERY_KEY, residenceId] })
    },
  })
}

/**
 * Déplacer un dossier (changer son parent)
 */
export function useMoveFolder() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      folderId,
      newParentId,
    }: {
      folderId: string
      newParentId: string | null
    }) => {
      const { data, error } = await supabase
        .from('document_folders')
        .update({ parent_folder_id: newParentId })
        .eq('id', folderId)
        .select()
        .single()

      if (error) throw error
      return data as DocumentFolder
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [...FOLDERS_QUERY_KEY, data.residence_id] })
    },
  })
}

/**
 * Créer les dossiers système par défaut pour une résidence
 */
export function useCreateDefaultFolders() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (residenceId: string) => {
      await typedSupabaseRpc.documents.createDefaultFoldersForResidence(residenceId)
      return { residenceId }
    },
    onSuccess: ({ residenceId }) => {
      queryClient.invalidateQueries({ queryKey: [...FOLDERS_QUERY_KEY, residenceId] })
    },
  })
}

/**
 * Récupérer le chemin complet d'un dossier
 */
export function useFolderPath(folderId?: string) {
  return useQuery({
    queryKey: [...FOLDERS_QUERY_KEY, 'path', folderId],
    queryFn: async () => {
      if (!folderId) return null
      return await typedSupabaseRpc.documents.getFolderPath(folderId)
    },
    enabled: !!folderId,
  })
}

/**
 * Count subfolders per parent folder
 * Returns a map of folder_id -> subfolder count
 */
export function useSubfolderCounts(residenceId?: string) {
  const { data: folders = [] } = useFolders(residenceId)

  return React.useMemo(() => {
    const counts: Record<string, number> = {}

    folders.forEach((folder: DocumentFolder) => {
      if (folder.parent_folder_id) {
        counts[folder.parent_folder_id] = (counts[folder.parent_folder_id] || 0) + 1
      }
    })

    return counts
  }, [folders])
}

// Import React for useMemo
import React from 'react'
